import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { auditTime, filter, groupBy, mergeMap } from 'rxjs/operators';
import { global } from '../global';
import { MetaApiService } from './meta-api.service';
import { MetaApiPrice } from './metaapi-realtime-ws.service';
import { Mt5GatewayStreamingConnection } from './mt5-gateway-streaming-connection';

export type MetaApiStreamStatus = 'idle' | 'connecting' | 'synced' | 'down';

/**
 * Precios bid/ask en vivo por el websocket de MetaAPI (streaming del SDK `metaapi.cloud-sdk`),
 * en lugar de sondear `GET .../symbols/{symbol}/current-price`. Con el Gateway MT5 local el SDK no
 * aplica: se usa su WebSocket (Mt5GatewayStreamingConnection), con la misma interfaz.
 *
 * Una sola conexión streaming por pestaña, compartida por todos los consumidores (multicharts,
 * cartera del broker). Cada símbolo se suscribe una sola vez aunque lo pidan varios: se lleva un
 * contador de referencias y solo se desuscribe cuando el último consumidor lo suelta.
 *
 * El SDK (~1.7 MB) se carga con import dinámico la primera vez que alguien pide precios, y la
 * conexión corre fuera de la zona de Angular: los ticks de FX llegan varias veces por segundo y
 * cada uno dispararía detección de cambios en toda la app. Se vuelve a la zona solo al emitir,
 * ya limitado por símbolo con auditTime.
 */
@Injectable({
  providedIn: 'root'
})
export class MetaApiStreamingService {

  private readonly ticks$ = new Subject<MetaApiPrice>();
  private readonly statusSubject = new BehaviorSubject<MetaApiStreamStatus>('idle');
  public readonly status$ = this.statusSubject.asObservable();

  /** Símbolo (tal como lo usa MetaAPI) -> nº de consumidores que lo piden. */
  private readonly refCounts = new Map<string, number>();

  private api: any = null;
  private connection: any = null;
  private listener: any = null;
  private connecting: Promise<any> | null = null;
  private connectedAccountId = '';
  private preferredAccountId = '';
  /** Se incrementa en cada cierre: una conexión que termina de abrir con generación vieja se descarta. */
  private generation = 0;
  private closeTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly lastStorageWrite = new Map<string, number>();

  /** Tras soltar el último símbolo se espera un poco antes de cerrar (reabrir un diálogo no reconecta). */
  private readonly CLOSE_GRACE_MS = 15_000;
  /** Tope de escrituras en localStorage (`nec_rt_{symbol}`) por símbolo. */
  private readonly STORAGE_WRITE_MS = 1000;

  /**
   * Si abrir la conexión falla, se reintenta con backoff (5 s, 10 s, 20 s… hasta 60 s) mientras
   * alguien pida precios. Los cortes con la conexión ya abierta los reintenta el propio SDK.
   */
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private retryDelayMs = 0;
  private readonly RETRY_MIN_MS = 5_000;
  private readonly RETRY_MAX_MS = 60_000;
  /** Si el SDK no recupera una conexión caída en este tiempo, se descarta y se abre otra desde cero. */
  private downWatchdog: ReturnType<typeof setTimeout> | null = null;
  private readonly DOWN_REBUILD_MS = 90_000;
  /** Mientras el streaming está caído, se sondea el precio por REST para no congelar las gráficas. */
  private restPoll: ReturnType<typeof setInterval> | null = null;
  private readonly REST_POLL_MS = 5_000;
  /**
   * El SDK tarda en enterarse de un corte (hasta 60 s sin paquete de estado) y reintenta con
   * backoff de hasta 30 s, así que tras volver la red puede pasar mucho sin ticks. Si el streaming
   * lleva STALE_MS sin ticks, se compara con el precio REST: si REST trae uno más nuevo, el socket
   * está muerto y se reabre. Con mercado cerrado REST tampoco avanza y no se reconecta en bucle.
   */
  private lastStreamTickAt = 0;
  private readonly lastStreamPriceTime = new Map<string, number>();
  private staleCheck: ReturnType<typeof setInterval> | null = null;
  private readonly STALE_MS = 45_000;
  private hiddenAt = 0;

  constructor(private metaApiService: MetaApiService, private ngZone: NgZone) {
    this.metaApiService.credentialsCleared$.subscribe(() => this.shutdown('down'));
    this.status$.subscribe(status => {
      console.info('[MetaApiStreaming] estado:', status);
      status === 'down' ? this.startRestPoll() : this.stopRestPoll();
    });
    if (typeof window !== 'undefined') {
      window.addEventListener('offline', () => {
        if (this.refCounts.size > 0 && this.statusSubject.value !== 'idle') this.statusSubject.next('down');
      });
      // Al volver la red no se espera al backoff del SDK: se reabre la conexión desde cero.
      window.addEventListener('online', () => this.forceReconnect('red recuperada'));
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          this.hiddenAt = Date.now();
        } else if (this.statusSubject.value === 'down' || (this.hiddenAt && Date.now() - this.hiddenAt > this.STALE_MS)) {
          this.checkStale(true);
        }
      });
    }
  }

  /**
   * Cuenta MetaAPI a usar. Por defecto la activa en localStorage; el broker la fija con la de la
   * cartera abierta. Si cambia con la conexión abierta, se reconecta a la nueva.
   */
  useAccount(accountId: string): void {
    if (!accountId || accountId === this.preferredAccountId) return;
    this.preferredAccountId = accountId;
    if ((this.connection || this.connecting) && accountId !== this.connectedAccountId) {
      this.shutdown('idle');
      if (this.refCounts.size > 0) this.ensureConnected().catch(() => {});
    }
  }

  /**
   * Ticks de los símbolos pedidos, limitados a uno cada `auditMs` por símbolo (el último).
   * Al suscribirse emite enseguida el último precio conocido de cada símbolo; al desuscribirse
   * libera los símbolos que ya nadie más usa.
   */
  prices$(isos: string[], auditMs = 250): Observable<MetaApiPrice> {
    const symbols = Array.from(new Set((isos || []).map(s => (s || '').trim()).filter(Boolean)));
    return new Observable<MetaApiPrice>(observer => {
      const wanted = new Set(symbols.map(s => s.toUpperCase()));
      const sub = this.ticks$.pipe(
        filter(p => wanted.has(p.symbol.toUpperCase())),
        groupBy(p => p.symbol),
        mergeMap(g => auditMs > 0 ? g.pipe(auditTime(auditMs)) : g)
      ).subscribe(p => this.ngZone.run(() => observer.next(p)));

      this.acquire(symbols);
      return () => {
        sub.unsubscribe();
        this.release(symbols);
      };
    });
  }

  /** Último precio conocido de un símbolo en el estado local del terminal, o null. */
  lastPrice(symbol: string): MetaApiPrice | null {
    try {
      const price = this.connection?.terminalState?.price(symbol);
      return price ? this.toPrice(price) : null;
    } catch {
      return null;
    }
  }

  // ---------------------------------------------------------------------------

  private acquire(symbols: string[]): void {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
    const nuevos: string[] = [];
    for (const s of symbols) {
      const n = (this.refCounts.get(s) ?? 0) + 1;
      this.refCounts.set(s, n);
      if (n === 1) nuevos.push(s);
    }

    if (this.connection && this.statusSubject.value === 'synced') {
      nuevos.forEach(s => this.subscribeSymbol(this.connection, s));
      // Los que ya estaban suscritos no vuelven a recibir su precio hasta el siguiente tick:
      // se siembra el nuevo consumidor con el último conocido.
      symbols.filter(s => !nuevos.includes(s)).forEach(s => this.emitSeed(this.connection, s));
    } else {
      // La conexión suscribe todo lo que haya en refCounts al terminar de abrir.
      this.ensureConnected().catch(() => {});
    }
  }

  private release(symbols: string[]): void {
    for (const s of symbols) {
      const n = (this.refCounts.get(s) ?? 0) - 1;
      if (n > 0) {
        this.refCounts.set(s, n);
        continue;
      }
      this.refCounts.delete(s);
      if (this.connection && this.statusSubject.value === 'synced') {
        this.ngZone.runOutsideAngular(() => {
          this.connection.unsubscribeFromMarketData(s, [{ type: 'quotes' }])
            .catch((err: any) => console.warn(`[MetaApiStreaming] unsubscribe ${s}`, err));
        });
      }
    }
    if (this.refCounts.size === 0) this.clearRetry();
    if (this.refCounts.size === 0 && !this.closeTimer) {
      this.closeTimer = setTimeout(() => {
        this.closeTimer = null;
        if (this.refCounts.size === 0) this.shutdown('idle');
      }, this.CLOSE_GRACE_MS);
    }
  }

  private ensureConnected(): Promise<any> {
    // Con la conexión abierta pero caída, el SDK ya está reconectando: no se abre una segunda.
    if (this.connection) return Promise.resolve(this.connection);
    if (this.connecting) return this.connecting;
    this.clearRetry(false);

    const token = this.metaApiService.getToken();
    const accountId = this.preferredAccountId || this.metaApiService.getAccountId();
    if (!token || !accountId) {
      this.statusSubject.next('down');
      return Promise.reject(new Error('Sin credenciales MetaAPI'));
    }

    const generation = this.generation;
    this.statusSubject.next('connecting');
    this.connecting = this.ngZone.runOutsideAngular(async () => {
      let api: any = null;
      let connection: any;
      let listenerBase: any = {};
      if (this.metaApiService.isGateway()) {
        // Gateway MT5 local: WebSocket propio con la misma interfaz que la conexión del SDK
        connection = new Mt5GatewayStreamingConnection(global.session.mt5GwWsUrl);
      } else {
        const mod: any = await import('metaapi.cloud-sdk');
        const MetaApi = mod.default ?? mod.MetaApi;
        api = new MetaApi(token);
        const account = await api.metatraderAccountApi.getAccount(accountId);
        connection = account.getStreamingConnection();
        listenerBase = new mod.SynchronizationListener();
      }
      // Hay un evento connected/disconnected por instancia (réplica): solo está caída si no queda ninguna.
      const instances = new Set<string>();
      const lost = (instanceIndex: string) => {
        instances.delete(instanceIndex);
        if (this.connection === connection && instances.size === 0) this.onConnectionLost(connection);
      };
      const listener = Object.assign(listenerBase, {
        onSymbolPriceUpdated: async (_instanceIndex: string, price: any) => this.onPrice(price),
        onConnected: async (instanceIndex: string) => {
          instances.add(instanceIndex);
          if (this.connection === connection) this.onConnectionRestored(connection);
        },
        onDisconnected: async (instanceIndex: string) => lost(instanceIndex),
        onStreamClosed: async (instanceIndex: string) => lost(instanceIndex)
      });
      connection.addSynchronizationListener(listener);

      const discard = () => {
        try { connection.removeSynchronizationListener(listener); } catch {}
        connection.close().catch(() => {});
        try { api?.close(); } catch {}
      };
      try {
        await connection.connect();
        await connection.waitSynchronized({ timeoutInSeconds: 60 });
      } catch (err) {
        discard();
        throw err;
      }
      if (generation !== this.generation) {
        discard();
        throw new Error('Conexión MetaAPI descartada');
      }

      this.api = api;
      this.connection = connection;
      this.listener = listener;
      this.connectedAccountId = accountId;
      this.retryDelayMs = 0;
      this.lastStreamTickAt = Date.now();
      this.startStaleCheck();
      this.ngZone.run(() => this.statusSubject.next('synced'));
      Array.from(this.refCounts.keys()).forEach(s => this.subscribeSymbol(connection, s));
      return connection;
    }).catch((err: any) => {
      if (generation === this.generation) {
        console.error('[MetaApiStreaming] no se pudo abrir el streaming', err);
        this.ngZone.run(() => this.statusSubject.next('down'));
        this.scheduleRetry();
      }
      throw err;
    }).finally(() => {
      if (generation === this.generation) this.connecting = null;
    });
    return this.connecting;
  }

  private onConnectionLost(connection: any): void {
    this.ngZone.run(() => this.statusSubject.next('down'));
    if (this.downWatchdog) return;
    this.downWatchdog = setTimeout(() => {
      this.downWatchdog = null;
      if (this.connection !== connection || this.statusSubject.value !== 'down') return;
      console.warn('[MetaApiStreaming] el SDK no recuperó la conexión; se abre una nueva');
      this.shutdown('down');
      if (this.refCounts.size > 0) this.ensureConnected().catch(() => {});
    }, this.DOWN_REBUILD_MS);
  }

  private onConnectionRestored(connection: any): void {
    this.clearDownWatchdog();
    this.retryDelayMs = 0;
    this.lastStreamTickAt = Date.now();
    this.startStaleCheck();
    this.ngZone.run(() => this.statusSubject.next('synced'));
    // El SDK resuscribe lo que tenía; falta cuadrar lo pedido o soltado mientras estaba caída.
    this.ngZone.runOutsideAngular(() => {
      connection.waitSynchronized({ timeoutInSeconds: 60 }).then(() => {
        if (this.connection !== connection) return;
        const subscribed = new Set<string>(connection.subscribedSymbols ?? []);
        this.refCounts.forEach((_n, s) => { if (!subscribed.has(s)) this.subscribeSymbol(connection, s); });
        subscribed.forEach(s => {
          if (!this.refCounts.has(s)) connection.unsubscribeFromMarketData(s, [{ type: 'quotes' }]).catch(() => {});
        });
      }).catch(() => {});
    });
  }

  private scheduleRetry(): void {
    if (this.retryTimer || this.refCounts.size === 0) return;
    if (!this.metaApiService.getToken()) return;
    this.retryDelayMs = this.retryDelayMs
      ? Math.min(this.retryDelayMs * 2, this.RETRY_MAX_MS)
      : this.RETRY_MIN_MS;
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      if (this.refCounts.size > 0) this.ensureConnected().catch(() => {});
    }, this.retryDelayMs);
  }

  /** Cierra lo que haya (conexión viva, caída o a medio abrir) y abre una nueva sin esperar backoff. */
  private forceReconnect(motivo: string): void {
    if (this.refCounts.size === 0 || this.statusSubject.value === 'idle') return;
    console.warn(`[MetaApiStreaming] reconexión forzada (${motivo})`);
    this.shutdown('down');
    this.ensureConnected().catch(() => {});
  }

  /**
   * Si el streaming lleva un rato sin ticks, pide por REST el precio de un símbolo: si es más
   * nuevo que el último tick recibido, el socket está muerto y se reabre.
   */
  private checkStale(force = false): void {
    if (this.refCounts.size === 0 || this.connecting) return;
    if (!this.connection) {
      if (this.statusSubject.value === 'down') this.forceReconnect('sin conexión');
      return;
    }
    if (!force && Date.now() - this.lastStreamTickAt < this.STALE_MS) return;
    // Se compara con el símbolo que más recientemente tuvo tick (hora de mercado de ambos lados).
    let symbol = this.refCounts.keys().next().value as string;
    let lastTime = 0;
    this.refCounts.forEach((_n, s) => {
      const t = this.lastStreamPriceTime.get(s) ?? 0;
      if (t > lastTime) { lastTime = t; symbol = s; }
    });
    const connection = this.connection;
    this.metaApiService.getCurrentPrice(this.connectedAccountId, symbol).subscribe(price => {
      if (!price || this.connection !== connection) return;
      const restTime = new Date(price.time).getTime();
      if (restTime > lastTime + 5_000) this.forceReconnect(`sin ticks de ${symbol}`);
    });
  }

  private startStaleCheck(): void {
    if (!this.staleCheck) this.staleCheck = setInterval(() => this.checkStale(), 15_000);
  }

  private stopStaleCheck(): void {
    if (this.staleCheck) {
      clearInterval(this.staleCheck);
      this.staleCheck = null;
    }
  }

  private clearRetry(resetDelay = true): void {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    if (resetDelay) this.retryDelayMs = 0;
  }

  private clearDownWatchdog(): void {
    if (this.downWatchdog) {
      clearTimeout(this.downWatchdog);
      this.downWatchdog = null;
    }
  }

  private startRestPoll(): void {
    if (this.restPoll) return;
    this.restPoll = setInterval(() => {
      const accountId = this.preferredAccountId || this.metaApiService.getAccountId();
      if (!accountId || this.refCounts.size === 0) return;
      this.refCounts.forEach((_n, symbol) => {
        this.metaApiService.getCurrentPrice(accountId, symbol).subscribe(price => {
          if (price && this.statusSubject.value === 'down') this.onPrice({ ...price, symbol }, false);
        });
      });
    }, this.REST_POLL_MS);
  }

  private stopRestPoll(): void {
    if (this.restPoll) {
      clearInterval(this.restPoll);
      this.restPoll = null;
    }
  }

  private subscribeSymbol(connection: any, symbol: string): void {
    this.ngZone.runOutsideAngular(() => {
      // Resuelve cuando llega la primera cotización (o a los 30 s), así que tras él ya hay precio.
      connection.subscribeToMarketData(symbol, [{ type: 'quotes' }])
        .then(() => this.emitSeed(connection, symbol))
        .catch((err: any) => console.warn(`[MetaApiStreaming] subscribe ${symbol}`, err));
    });
  }

  private emitSeed(connection: any, symbol: string): void {
    try {
      const price = connection.terminalState?.price(symbol);
      if (price) this.onPrice(price);
    } catch {}
  }

  private onPrice(raw: any, fromStream = true): void {
    if (!raw?.symbol || !this.refCounts.has(raw.symbol)) return;
    const price = this.toPrice(raw);

    const now = Date.now();
    if (fromStream) {
      this.lastStreamTickAt = now;
      const t = new Date(price.time).getTime();
      if (t > (this.lastStreamPriceTime.get(price.symbol) ?? 0)) this.lastStreamPriceTime.set(price.symbol, t);
    }
    if (now - (this.lastStorageWrite.get(price.symbol) ?? 0) >= this.STORAGE_WRITE_MS) {
      this.lastStorageWrite.set(price.symbol, now);
      try {
        localStorage.setItem(`nec_rt_${price.symbol}`, JSON.stringify({ ...price, _ts: now }));
      } catch {}
    }
    this.ticks$.next(price);
  }

  private toPrice(raw: any): MetaApiPrice {
    const bid = Number(raw.bid ?? raw.ask);
    const ask = Number(raw.ask ?? raw.bid);
    const time = raw.time instanceof Date ? raw.time.toISOString() : (raw.time ? String(raw.time) : new Date().toISOString());
    const price: MetaApiPrice = { symbol: raw.symbol, bid, ask, cotizacion: bid, time };
    if (raw.brokerTime) price.brokerTime = String(raw.brokerTime);
    // Para el beneficio en la divisa de la cuenta (MetaAPI y el gateway los envían en cada precio)
    if (raw.profitTickValue != null) price.profitTickValue = Number(raw.profitTickValue);
    if (raw.lossTickValue != null) price.lossTickValue = Number(raw.lossTickValue);
    return price;
  }

  private shutdown(status: MetaApiStreamStatus): void {
    this.generation++;
    this.connecting = null;
    this.clearRetry();
    this.clearDownWatchdog();
    this.stopStaleCheck();
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
    const connection = this.connection;
    const listener = this.listener;
    const api = this.api;
    this.connection = null;
    this.listener = null;
    this.api = null;
    this.connectedAccountId = '';
    if (connection) {
      this.ngZone.runOutsideAngular(() => {
        try { if (listener) connection.removeSynchronizationListener(listener); } catch {}
        connection.close().catch(() => {});
        try { api?.close(); } catch {}
      });
    }
    this.statusSubject.next(status);
  }
}
